import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ArrowLeft, CircleNotch, TrendUp, TrendDown,
  CurrencyDollar, Briefcase, HardHat, ClipboardText,
  Users, Bell, Envelope, ArrowsClockwise, ChartLineUp,
  Target, Fire, CalendarCheck, Warning, Timer,
  Funnel, Receipt, Package, PaperPlaneTilt, CheckCircle
} from '@phosphor-icons/react';

export default function ExecutiveDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/executive-scorecard`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSend = async () => {
    const emails = emailInput.split(',').map(e => e.trim()).filter(e => e.includes('@'));
    if (emails.length === 0) return;
    setSending(true);
    setSendResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/dashboard/send-executive-scorecard`, { emails }, { withCredentials: true });
      setSendResult({ ok: true, msg: res.data.message });
    } catch (err) {
      setSendResult({ ok: false, msg: err.response?.data?.detail || 'Error al enviar' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-12 text-slate-500">Error al cargar el scorecard</div>;
  }

  const { sales, b2b, projects, operations, hrms, finance } = data;

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-btn">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Scorecard Ejecutivo</h1>
            <p className="text-sm text-slate-500">
              Vision consolidada de todos los modulos | {new Date(data.generated_at).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowEmailModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors" data-testid="send-email-btn">
            <PaperPlaneTilt size={16} /> Enviar por Email
          </button>
          <button onClick={fetchData} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg" data-testid="refresh-btn">
            <ArrowsClockwise size={20} />
          </button>
        </div>
      </div>

      {/* VENTAS - Hero */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white" data-testid="sales-section">
        <div className="flex items-center gap-2 mb-4">
          <CurrencyDollar size={20} className="text-blue-400" />
          <h2 className="text-lg font-semibold">Ventas</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <MetricCard
            label="Ingresos del Mes"
            value={`$${sales.revenue_month.toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
            change={sales.revenue_change_pct}
            dark
          />
          <MetricCard label="Pipeline Activo" value={`$${sales.pipeline_value.toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`} dark />
          <MetricCard label="Leads Nuevos (7d)" value={sales.new_leads_week} icon={Target} dark />
          <MetricCard label="Cotizaciones Firmadas" value={sales.quotes_signed} subtext={`${sales.close_rate}% cierre`} dark />
          <MetricCard label="Pendientes" value={sales.quotes_pending} icon={Funnel} dark />
        </div>
      </div>

      {/* B2B + PROYECTOS */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* B2B */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="b2b-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-lg bg-violet-50 text-violet-600"><Briefcase size={18} /></div>
            <h2 className="text-base font-semibold text-slate-900">Prospeccion B2B</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MiniCard label="Total Prospectos" value={b2b.total_prospects} color="text-violet-600" />
            <MiniCard label="Nuevos (7d)" value={b2b.new_this_week} color="text-blue-600" />
            <MiniCard label="Hot" value={b2b.hot_prospects} color="text-red-500" />
            <MiniCard label="Contactados (7d)" value={b2b.contacted_this_week} color="text-emerald-600" />
            <MiniCard label="Convertidos (mes)" value={b2b.converted_this_month} color="text-green-600" />
            <MiniCard label="Seguimientos" value={b2b.followups_due} color={b2b.followups_due > 0 ? 'text-red-500' : 'text-slate-600'} alert={b2b.followups_due > 0} />
          </div>
          {b2b.active_campaigns > 0 && (
            <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
              <Envelope size={12} /> {b2b.active_campaigns} campanas activas
            </p>
          )}
        </div>

        {/* PROYECTOS */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="projects-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-lg bg-amber-50 text-amber-600"><HardHat size={18} /></div>
            <h2 className="text-base font-semibold text-slate-900">Proyectos</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-900">{projects.active}</p>
              <p className="text-xs text-slate-500 mt-1">Activos</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">{projects.completed_month}</p>
              <p className="text-xs text-slate-500 mt-1">Completados (mes)</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-slate-900">${(projects.total_value / 1000).toFixed(0)}k</p>
              <p className="text-xs text-slate-500 mt-1">Valor Activo</p>
            </div>
          </div>
        </div>
      </div>

      {/* OPERACIONES + RRHH */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* OPERACIONES */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="operations-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-lg bg-green-50 text-green-600"><ClipboardText size={18} /></div>
            <h2 className="text-base font-semibold text-slate-900">Operaciones</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MiniCard label="OTs Activas" value={operations.wo_active} color="text-blue-600" />
            <MiniCard label="Completadas (7d)" value={operations.wo_completed_week} color="text-green-600" />
            <MiniCard label="Completadas (mes)" value={operations.wo_completed_month} color="text-emerald-600" />
            <MiniCard label="Citas Pendientes" value={operations.pending_bookings} color={operations.pending_bookings > 0 ? 'text-amber-500' : 'text-slate-600'} />
          </div>
          {operations.confirmed_bookings > 0 && (
            <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
              <CalendarCheck size={12} /> {operations.confirmed_bookings} citas confirmadas
            </p>
          )}
        </div>

        {/* RRHH */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="hrms-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-lg bg-pink-50 text-pink-600"><Users size={18} /></div>
            <h2 className="text-base font-semibold text-slate-900">Recursos Humanos</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MiniCard label="Empleados Activos" value={hrms.active_employees} color="text-slate-700" />
            <MiniCard label="En Turno Hoy" value={hrms.clocked_in_today} color="text-green-600" />
            <MiniCard label="PTO Pendientes" value={hrms.pto_pending} color={hrms.pto_pending > 0 ? 'text-amber-500' : 'text-slate-600'} alert={hrms.pto_pending > 0} />
            <MiniCard label="PTO Aprobados (mes)" value={hrms.pto_approved_month} color="text-blue-600" />
          </div>
        </div>
      </div>

      {/* ALERTAS FINANCIERAS */}
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="alerts-section">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 rounded-lg bg-red-50 text-red-600"><Warning size={18} /></div>
          <h2 className="text-base font-semibold text-slate-900">Alertas y Finanzas</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <AlertCard label="Por Cobrar" value={`$${finance.invoices_pending_value.toLocaleString('en', {minimumFractionDigits: 0, maximumFractionDigits: 0})}`} sub={`${finance.invoices_pending_count} facturas`} color={finance.invoices_pending_count > 0 ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'} />
          <AlertCard label="Facturas Vencidas" value={finance.invoices_overdue} color={finance.invoices_overdue > 0 ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'} />
          <AlertCard label="Stock Bajo" value={finance.low_stock_alerts} sub="productos" color={finance.low_stock_alerts > 0 ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'} />
          <AlertCard label="Total Clientes" value={sales.total_clients} color="border-blue-200 bg-blue-50" />
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEmailModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="email-modal">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Enviar Scorecard por Email</h3>
            <p className="text-sm text-slate-500 mb-4">Ingresa los correos separados por coma.</p>
            <input
              type="text"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder="email@ejemplo.com, otro@ejemplo.com"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="email-input"
            />
            {sendResult && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${sendResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="send-result">
                {sendResult.ok ? <CheckCircle size={16} className="inline mr-1" /> : null}
                {sendResult.msg}
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowEmailModal(false)} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="cancel-btn">
                Cancelar
              </button>
              <button onClick={handleSend} disabled={sending} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="send-btn">
                {sending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
                {sending ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, change, subtext, icon: Icon, dark }) {
  return (
    <div className={`rounded-xl p-4 ${dark ? 'bg-white/10 backdrop-blur-sm' : 'bg-white border border-slate-200'}`}>
      <div className="flex items-center justify-between mb-1">
        <p className={`text-xs ${dark ? 'text-slate-300' : 'text-slate-500'}`}>{label}</p>
        {Icon && <Icon size={14} className={dark ? 'text-slate-400' : 'text-slate-400'} />}
      </div>
      <p className={`text-xl font-bold ${dark ? 'text-white' : 'text-slate-900'}`}>{value}</p>
      {change !== undefined && (
        <div className="flex items-center gap-1 mt-1">
          {change >= 0 ? <TrendUp size={12} className="text-emerald-400" /> : <TrendDown size={12} className="text-red-400" />}
          <span className={`text-xs font-medium ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {change >= 0 ? '+' : ''}{change}% vs mes ant.
          </span>
        </div>
      )}
      {subtext && <p className={`text-xs mt-1 ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{subtext}</p>}
    </div>
  );
}

function MiniCard({ label, value, color, alert }) {
  return (
    <div className={`rounded-lg p-3 ${alert ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function AlertCard({ label, value, sub, color }) {
  return (
    <div className={`rounded-xl border p-4 ${color}`}>
      <p className="text-xs text-slate-600 mb-1">{label}</p>
      <p className="text-xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
